import { cp, mkdir, readdir, readFile, rm, stat, writeFile } from "node:fs/promises";
import { existsSync } from "node:fs";
import { join } from "node:path";

const SKIP = new Set([
  "node_modules",
  ".next",
  ".turbo",
  ".vercel",
  "dist",
  ".git",
  "test-results",
  "playwright-report",
]);

const cpFilter = (s: string) => {
  const base = s.split("/").pop() ?? "";
  return !SKIP.has(base);
};

/** Recursively copy a directory's contents into `dest`, skipping build artifacts and VCS noise. */
export async function copyTree(src: string, dest: string): Promise<void> {
  await mkdir(dest, { recursive: true });
  for (const entry of await readdir(src, { withFileTypes: true })) {
    if (SKIP.has(entry.name)) continue;
    await cp(join(src, entry.name), join(dest, entry.name), {
      recursive: true,
      filter: cpFilter,
    });
  }
}

/** Remove paths under `dest` that are absent from `src` (mirror sync after `copyTree`). */
export async function pruneTree(src: string, dest: string): Promise<void> {
  if (!(await isDir(dest))) return;
  for (const entry of await readdir(dest, { withFileTypes: true })) {
    if (SKIP.has(entry.name)) continue;
    const srcPath = join(src, entry.name);
    const destPath = join(dest, entry.name);
    if (!existsSync(srcPath)) {
      await rm(destPath, { recursive: true, force: true });
      continue;
    }
    if (entry.isDirectory() && (await isDir(srcPath))) await pruneTree(srcPath, destPath);
  }
}

/** Walk every file under `dir` and yield absolute paths. */
export async function* walk(dir: string): AsyncGenerator<string> {
  for (const entry of await readdir(dir, { withFileTypes: true })) {
    if (SKIP.has(entry.name)) continue;
    const full = join(dir, entry.name);
    if (entry.isDirectory()) yield* walk(full);
    else yield full;
  }
}

const TEXT_EXT = new Set([
  ".ts", ".tsx", ".js", ".jsx", ".mjs", ".cjs",
  ".json", ".css", ".md", ".mdx", ".html", ".txt",
  ".env", ".example", ".yml", ".yaml", ".toml",
]);

export function isTextFile(path: string): boolean {
  const dot = path.lastIndexOf(".");
  if (dot === -1) return false;
  return TEXT_EXT.has(path.slice(dot));
}

/** Apply ordered [find, replace] string substitutions to every text file under `dir`. */
export async function replaceInTree(
  dir: string,
  subs: ReadonlyArray<readonly [from: string | RegExp, to: string]>,
): Promise<number> {
  let touched = 0;
  for await (const file of walk(dir)) {
    if (!isTextFile(file)) continue;
    const before = await readFile(file, "utf8");
    let after = before;
    for (const [from, to] of subs) {
      after = typeof from === "string" ? after.split(from).join(to) : after.replace(from, to);
    }
    if (after !== before) {
      await writeFile(file, after);
      touched++;
    }
  }
  return touched;
}

export async function exists(p: string): Promise<boolean> {
  return existsSync(p);
}

export async function isDir(p: string): Promise<boolean> {
  try {
    return (await stat(p)).isDirectory();
  } catch {
    return false;
  }
}

export { writeFile, mkdir, readFile, readdir };
